import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { currentUserIdState } from '@/state';
import logger from '@/util/logger';
import { ActionError, triggerEquipmentDetailsRefresh, updateEquipmentAction } from '@rock/actions';
import { createId, EquipmentDao, EquipmentRefreshDao, FolderDao, RoomDao } from '@rock/db-admin';
import { type Equipment, imageForCategory, orderedImages, UserIdSchema } from '@rock/types';
import { equipmentIdentity } from '@rock/util';
import { useAtomValue } from 'jotai';
import { MoreHorizontal } from 'lucide-react';
import { type FC, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { EntryFormDialog, type EntryPrefill } from '../equipmentTestLibrary/EntryFormDialog';

const log = logger('EquipmentActionsMenu');

// The `update-equipment` action's DAO bag. Folder/room are only touched when the input carries a
// folderId/roomId (never from this dialog), but the action declares them, so provide them all.
const updateEquipmentDaos = {
    equipment: new EquipmentDao(),
    equipmentRefresh: new EquipmentRefreshDao(),
    folder: new FolderDao(),
    room: new RoomDao(),
};

/**
 * Kebab menu of admin actions on one piece of user equipment. Rendered by `EquipmentCard`, so it
 * lights up everywhere equipment appears (home equipment tab, model page Equipment tab).
 */
export const EquipmentActionsMenu: FC<{ equipment: Equipment }> = ({ equipment }) => {
    const [editOpen, setEditOpen] = useState(false);
    const [libraryOpen, setLibraryOpen] = useState(false);
    // Memoized so the dialog sees one prefill per equipment, not a fresh object per render.
    const libraryPrefill = useMemo(() => toLibraryPrefill(equipment), [equipment]);

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label="More actions">
                        <MoreHorizontal className="h-4 w-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    <DropdownMenuItem
                        disabled={!equipmentIdentity(equipment)}
                        onSelect={() => void refreshModelSpecs(equipment)}
                    >
                        Refresh Model Specs
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => setEditOpen(true)}>Edit model number…</DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => setLibraryOpen(true)}>Add to Test Library…</DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
            <EditModelNumberDialog open={editOpen} onOpenChange={setEditOpen} equipment={equipment} />
            <EntryFormDialog open={libraryOpen} onOpenChange={setLibraryOpen} prefill={libraryPrefill} />
        </>
    );
};

/**
 * Re-run the unit's identity refresh, which joins it afresh under the rules as they stand now — after a
 * brand review, a type change or a family edit. The details stay on screen while it runs.
 */
async function refreshModelSpecs(equipment: Equipment) {
    try {
        await triggerEquipmentDetailsRefresh({
            equipment: updateEquipmentDaos.equipment,
            equipmentRefresh: updateEquipmentDaos.equipmentRefresh,
            ctx: { homeId: equipment.homeId, idGen: createId, logger: log },
            equipmentId: equipment.id,
            clearStale: false,
        });
        toast.success('Refresh queued. The unit joins afresh under today’s rules.');
    } catch (err) {
        log.error('Failed to request an equipment refresh', {
            err,
            equipmentId: equipment.id,
            homeId: equipment.homeId,
        });
        toast.error('Unable to queue the refresh', { description: err instanceof Error ? err.message : undefined });
    }
}

/**
 * Lifts a real piece of a user's equipment into a test-library draft: type, brand, model, serial and
 * both photos, so a genuine unit someone actually owns becomes test material with one click. Real
 * photos beat anything a web search turns up — right glare, right angle, right grime.
 *
 * The photos still point at the home's Storage objects here. The `rehostTestLibraryImages` trigger
 * copies them under the new entry once it's saved, so the library owns its own bytes and a user
 * deleting their equipment can't hollow out the entry.
 */
function toLibraryPrefill(equipment: Equipment): EntryPrefill {
    // The scan flow tags the plate photo, so ask for it by category rather than guessing; fall back
    // to capture order (the unit first, its plate second) for equipment entered before that existed.
    const images = orderedImages(equipment);
    const nameplate = imageForCategory(equipment, 'model-number') ?? images[1];
    const product = images.find((image) => image.id !== nameplate?.id) ?? images[0];
    const brand = equipment.fields?.brand;
    return {
        spaceId: equipment.spaceId,
        typeId: equipment.typeId,
        name: [brand, equipmentIdentity(equipment)?.display].filter(Boolean).join(' '),
        brand,
        modelNumber: equipment.fields?.modelNumber,
        serialNumber: equipment.fields?.serialNumber,
        productImage: product,
        nameplateImage: nameplate,
    };
}

/**
 * Corrects the user-typed model number through the same `update-equipment` action the app uses, so
 * the change rides the full re-identification pipeline: stale `details`/`sources` are cleared and a
 * background refresh re-resolves the equipment to the right catalog model (creating it if needed).
 */
const EditModelNumberDialog: FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    equipment: Equipment;
}> = ({ open, onOpenChange, equipment }) => {
    const currentModelNumber = equipment.fields?.modelNumber ?? '';
    const [modelNumber, setModelNumber] = useState<string>(currentModelNumber);
    const [saving, setSaving] = useState(false);
    const userId = useAtomValue(currentUserIdState);

    // Reset to the doc's current value each time the dialog opens.
    useEffect(() => {
        if (open) setModelNumber(currentModelNumber);
    }, [open, currentModelNumber]);

    const submit = async () => {
        const trimmed = modelNumber.trim();
        if (!trimmed) {
            toast.error('Enter a model number');
            return;
        }
        if (trimmed === currentModelNumber) {
            onOpenChange(false);
            return;
        }
        if (!userId) {
            toast.error('Not signed in');
            return;
        }
        setSaving(true);
        try {
            const input = updateEquipmentAction.input.parse({ equipmentId: equipment.id, modelNumber: trimmed });
            await updateEquipmentAction.execute(input, {
                userId: UserIdSchema.parse(userId),
                homeId: equipment.homeId,
                logger: log,
                idGen: createId,
                daos: updateEquipmentDaos,
            });
            toast.success('Model number updated — identification refresh queued');
            onOpenChange(false);
        } catch (err) {
            log.error('Failed to update model number', { err, equipmentId: equipment.id });
            toast.error(err instanceof ActionError ? err.userMessage : 'Failed to update model number');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Edit model number</DialogTitle>
                    <DialogDescription>
                        Corrects the model number on this user's equipment — the same edit they could make in the app.
                        Saving clears the current catalog snapshot and queues a background re-identification, which
                        repoints the equipment to the matching model (creating it if it doesn't exist yet).
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-2">
                    <Label htmlFor="equipment-model-number">Model number</Label>
                    <Input
                        id="equipment-model-number"
                        value={modelNumber}
                        onChange={(event) => setModelNumber(event.target.value)}
                        disabled={saving}
                        placeholder="RUC80i"
                        autoComplete="off"
                        autoFocus
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') void submit();
                        }}
                    />
                </div>
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button type="button" onClick={() => void submit()} disabled={saving}>
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
